import React, { useState } from 'react';
import styled from 'styled-components';

const FooterRoot = styled.footer`
  padding: 4rem 1.5rem 2rem;
  background: linear-gradient(180deg, rgba(232, 232, 237, 0) 0%, #e8e8ed 22%, #e8e8ed 100%);

  @media (max-width: 700px) {
    padding: 3rem 1rem 1.5rem;
  }
`;

const Inner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  align-items: end;
  margin-bottom: 1.2rem;

  h2 { font-size: 1.55rem; font-weight: 650; }
  p { color: ${props => props.theme.colors.textSecondary}; font-size: 0.76rem; }

  @media (max-width: 620px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.35rem;
  }
`;

const Citation = styled.div`
  position: relative;
  overflow: hidden;
  border: 1px solid #2d3945;
  border-radius: 1.25rem;
  background: #111820;
  box-shadow: ${props => props.theme.shadows.md};
`;

const CitationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid #2d3945;
  color: #9eabb7;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.68rem;
`;

const CitationText = styled.pre`
  margin: 0;
  padding: 1.25rem;
  border-radius: 0;
  color: #d7dee8;
  font-size: 0.74rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CopyButton = styled.button`
  padding: 0.35rem 0.6rem;
  border: 1px solid #53606c;
  border-radius: 999px;
  background: transparent;
  color: #d7dee8;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.64rem;
  cursor: pointer;

  &:hover { border-color: #8ba7be; color: #fff; }
`;

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.7rem;

  .links { display: flex; gap: 1rem; }

  @media (max-width: 600px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const citationText = `@misc{luo2026harness,
  title         = {Your Harness is Not Secure: Benchmarking Real-world Threat of Command Line Interface Agent},
  author        = {Weidi Luo and Qiming Zhang and Tianyu Lu and Xiaogeng Liu and Bin Hu and Hung-Chun Chiu and Siyuan Ma and Yizhe Zhang and Xusheng Xiao and Yinzhi Cao and Zhen Xiang and Chaowei Xiao},
  year          = {2026},
  eprint        = {2510.06607},
  archivePrefix = {arXiv},
  primaryClass  = {cs.CR},
  note          = {Accepted by EMNLP 2026 Findings}
}`;

export const Footer: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(citationText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error('Unable to copy citation', error);
    }
  };

  return (
    <FooterRoot>
      <Inner>
        <Heading>
          <h2>Citation</h2>
        </Heading>
        <Citation>
          <CitationBar><span>bibtex</span><CopyButton onClick={copy}>{copied ? 'Copied' : 'Copy'}</CopyButton></CitationBar>
          <CitationText>{citationText}</CitationText>
        </Citation>
        <Bottom>
          <span>For controlled and defensive security research.</span>
          <div className="links">
            <a href="https://arxiv.org/abs/2510.06607" target="_blank" rel="noopener noreferrer">Paper ↗</a>
            <a href="https://github.com/SaFo-Lab/AdvCLI" target="_blank" rel="noopener noreferrer">Code ↗</a>
          </div>
        </Bottom>
      </Inner>
    </FooterRoot>
  );
};
